import { useMemo, type RefObject } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppWindow } from "lucide-react";
import { Badge, ChipList, FeedbackState, FormDrawer, Mono } from "@h2j-data-team/ui";
import type { QvdConsumingAppsResponse, QvdFile, UnloadedColumnsQvd } from "@repo/shared";

import {
  qvdConsumingAppsQueryOptions,
  qvdFileConsumingAppsQueryOptions,
} from "@/features/qvd-analysis/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { cn } from "@/lib/utils";
import { FileFormatBadge, ListError, ListLoading } from "./qvd-list-parts";

// An app with no fields needs a different sentence depending on WHY: with a
// readable reload log the file is loaded without field names (absence of
// evidence), while an unreadable log means the fields were never observed at
// all — and, for "forbidden", that the user can fix it. Rows persisted before
// logStatus existed carry unreadable-log apps too (that WAS the bug), but
// nothing in the stored blob tells them apart, so an EMPTY status keeps the
// readable-log wording until the run is re-analyzed.
const NO_FIELDS_NOTE: Record<string, string> = {
  forbidden: "columns.consumers.logForbidden",
  no_successful_reload: "columns.consumers.logNoReload",
  unreadable: "columns.consumers.logUnreadable",
};

// noFieldsNote picks that sentence. The fallback keys off whether logStatus is
// EMPTY, never off the map: a non-empty value the map does not know (a new
// bucket in the Go worker, a rename, a typo) still means "the log could not be
// read", so it must land on the generic unreadable wording — falling back to
// "loads the file without listing field names" would state, of an app whose log
// was never read, the one thing this run cannot know.
//
// "no_successful_reload" gets the dated variant when the run captured the app's
// last reload: the app HAS reloaded (the scan only ever looks at apps that
// have), the log for it is simply gone, and the date is what makes that
// concrete instead of leaving the reader to assume the app is dead. No date —
// an older run, persisted before the field existed — keeps the generic wording.
function noFieldsNote(logStatus: string, hasLastReload: boolean): string {
  if (!logStatus) return "columns.consumers.notDetailed";
  if (logStatus === "no_successful_reload" && hasLastReload) {
    return "columns.consumers.logNoReloadSince";
  }
  return NO_FIELDS_NOTE[logStatus] ?? "columns.consumers.logUnreadable";
}

// An app with a logStatus is listed for a reason INDEPENDENT of its (unread)
// reload log, and the row has to say so — otherwise "this app's log could not be
// read" reads as an unsourced claim that it reads the file at all. The worker
// leaves no ambiguity: an app whose log is unreadable contributes no read of its
// own (qvd_consumption.go skips it entirely), so it only ever reaches a
// consumer list through the resource-lineage merge in qvd_orphan_from_logs.go —
// where a failed lineage query drops the app instead of listing it. A non-empty
// logStatus therefore MEANS "Qlik's lineage put it here", with no extra field to
// carry.
//
// "no_successful_reload" gets its own sentence because the claim is weaker: that
// lineage is derived from the app's SCRIPT, and for this app there is no reload
// log left to corroborate it — Qlik expires them, so the log is gone, not
// missing because the app never ran (the scan only ever sees apps that have
// reloaded).
function lineageLinkNote(logStatus: string): string {
  return logStatus === "no_successful_reload"
    ? "columns.consumers.linkedByScript"
    : "columns.consumers.linkedByLineage";
}

// The drawer body, shared by every row type that has consuming apps: it knows
// how to render the payload and nothing about where it came from. The caller
// owns the query (which route, and when it may run), the header labels, and the
// optional `notice` that replaces the list when the row carries no evidence BY
// CONSTRUCTION. `showFields` is off for the file inventory, where `fields` is
// always empty for a reason that has nothing to do with the app: only the
// column phase reads the QVD header, so there is no per-field evidence to
// interpret — and an empty set there must not be read as "loads everything".
function ConsumingAppsSheet({
  open,
  onOpenChange,
  name,
  path,
  query: { data, isLoading, isError, error, refetch },
  notice = null,
  showFields = true,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string | undefined;
  path: string | undefined;
  query: UseQueryResult<QvdConsumingAppsResponse>;
  notice?: { title: string; description: string } | null;
  showFields?: boolean;
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t, i18n } = useTranslation("qvd-analysis");
  const { t: tc } = useTranslation("common");
  const { formatDate } = useFormatDate();
  // Space, then app — alphabetically, and locale-aware: a raw sort() would file
  // "Órgãos" after "Zona" in pt-BR. Apps whose space could not be resolved sink
  // to the bottom instead of leading the list with a blank group.
  const apps = useMemo(() => {
    const collator = new Intl.Collator(i18n.language, { sensitivity: "base", numeric: true });
    return [...(data?.consumedByApps ?? [])].sort((a, b) => {
      if (!a.spaceName !== !b.spaceName) return a.spaceName ? -1 : 1;
      return (
        collator.compare(a.spaceName, b.spaceName) ||
        collator.compare(a.appName || a.appId, b.appName || b.appId)
      );
    });
  }, [data?.consumedByApps, i18n.language]);

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
      title={
        <span className="flex items-center gap-2">
          <Mono className="truncate">{name}</Mono>
          <FileFormatBadge path={path ?? name} />
        </span>
      }
      description={
        <Mono className="block truncate" title={path}>
          {path}
        </Mono>
      }
    >
      {notice ? (
        <FeedbackState
          state="empty"
          icon={<AppWindow />}
          title={notice.title}
          description={notice.description}
        />
      ) : isLoading ? (
        <ListLoading />
      ) : isError ? (
        <ListError
          error={error}
          title={t("columns.consumers.error")}
          onRetry={() => void refetch()}
        />
      ) : apps.length === 0 ? (
        <FeedbackState
          state="empty"
          icon={<AppWindow />}
          title={t("columns.consumers.empty.title")}
          description={t("columns.consumers.empty.description")}
        />
      ) : (
        <>
          <p className="pb-2 text-sm text-dim">
            {t("columns.consumers.count", { count: apps.length })}
          </p>

          <ul className="overflow-hidden rounded-lg border border-hairline">
            {apps.map((app) => (
              <li
                key={app.appId}
                className="border-b border-hairline-soft px-3 py-(--tbl-cell-py) last:border-b-0"
              >
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {app.appName || app.appId}
                    </span>
                    {/* Space NAME when it resolved; otherwise the raw ID, which
                        is still searchable in Qlik — an app whose log was
                        forbidden usually has no resolvable name, and that is
                        exactly when the label has to stay useful. */}
                    <span
                      className={cn(
                        "block truncate text-xs text-faint",
                        !app.spaceName && app.spaceId && "font-mono",
                      )}
                      title={app.spaceName || app.spaceId || undefined}
                    >
                      {app.spaceName || app.spaceId || t("columns.consumers.unknownSpace")}
                    </span>
                    {/* Same vocabulary as the drawer header (name + mono
                        identifier): app names repeat across spaces, the ID
                        does not. */}
                    <Mono className="block truncate text-xs text-faint" title={app.appId}>
                      {app.appId}
                    </Mono>
                  </div>
                  {/* One badge for both: a wildcard read and a family
                      extrapolation are "inherited" to the reader. The two are
                      kept apart in the DATA (viaPattern is measured, viaFamily
                      is inferred), not on screen. */}
                  {(app.viaPattern || app.viaFamily) && (
                    <Badge
                      variant="secondary"
                      className="shrink-0"
                      title={t("columns.consumers.viaPatternHint")}
                    >
                      {t("columns.consumers.viaPattern")}
                    </Badge>
                  )}
                  {/* A different axis from the one above: those say where the
                      fields came from, this says how MUCH of the file the app
                      reads. `LOAD *` reads every column, which is the reason
                      this QVD promises no column savings. */}
                  {app.viaWholeFile && (
                    <Badge
                      variant="secondary"
                      className="shrink-0"
                      title={t("columns.consumers.viaWholeFileHint")}
                    >
                      {t("columns.consumers.viaWholeFile")}
                    </Badge>
                  )}
                </div>

                {/* Two independent facts, split so neither borrows the other's
                    authority: WHY this app is on the list at all (lineage — see
                    lineageLinkNote), and why its fields are unknown (its reload
                    log). Only an app with a logStatus gets the first line; for
                    every other app the log itself proved the read, which the
                    footnote already covers. */}
                {app.logStatus && (
                  <p className="mt-1.5 text-xs text-faint">{t(lineageLinkNote(app.logStatus))}</p>
                )}

                {/* Empty field set = the app reads the file but this run has no
                    per-field evidence for it — a LOAD * with no field list, or a
                    reload log that could not be read at all (logStatus). Never
                    "it loads nothing" — and never shown at all where the whole
                    field dimension is out of scope (showFields), since there the
                    emptiness says nothing about the app. */}
                {!showFields ? null : app.fields.length === 0 ? (
                  <p className="mt-1.5 text-xs text-faint">
                    {t(noFieldsNote(app.logStatus, !!app.lastReloadTime), {
                      date: app.lastReloadTime ? formatDate(app.lastReloadTime) : "",
                    })}
                  </p>
                ) : (
                  <ChipList
                    className="mt-2 [&>li>*]:font-mono"
                    aria-label={t("columns.consumers.fieldsList", {
                      name: app.appName || app.appId,
                    })}
                    items={app.fields.map((field) => ({ id: field, label: field }))}
                  />
                )}
              </li>
            ))}
          </ul>

          <p className="pt-3 text-xs text-faint">{t("columns.consumers.footnote")}</p>
        </>
      )}
    </FormDrawer>
  );
}

// Per-QVD detail drawer opened from a row of the unloaded-columns table: which
// apps consume the file and which of its fields each app loads. The detail is
// fetched lazily (only while open) and never for "inferred" or "not_analyzable"
// rows — the former were extrapolated from a name-family SAMPLE, the latter were
// never classified at all, so both carry no per-app evidence by construction.
// "family" rows are deliberately NOT suppressed: they do carry per-app detail,
// and that detail is the whole point — it is the evidence behind the
// extrapolation the user is being asked to judge. Neither are
// evidenceIncomplete rows (the "Incomplete evidence" tab opens this same
// drawer): showing WHICH app went mute and why is the entire reason those rows
// are kept, so they must never join the suppression list.
export function QvdConsumingAppsDrawer({
  open,
  onOpenChange,
  runId,
  row,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  row: UnloadedColumnsQvd | null;
  /** The button of the row that opened the drawer, for the focus to return to. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("qvd-analysis");
  const inferred = row?.analysisSource === "inferred";
  const notAnalyzable = row?.status === "not_analyzable";
  const query = useQuery(
    qvdConsumingAppsQueryOptions(runId, row?.id ?? "", open && !inferred && !notAnalyzable),
  );

  return (
    <ConsumingAppsSheet
      open={open}
      onOpenChange={onOpenChange}
      name={row?.qvdName}
      path={row?.qvdPath}
      query={query}
      restoreFocusRef={restoreFocusRef}
      notice={
        notAnalyzable
          ? {
              title: t("columns.notAnalyzable.chip"),
              description: t("columns.notAnalyzable.note"),
            }
          : inferred
            ? {
                title: t("columns.consumers.inferred.title"),
                description: t("columns.consumers.inferred.description"),
              }
            : null
      }
    />
  );
}

// The same drawer opened from a row of the file inventory. No suppression gates
// here — the row is only clickable when the run counted consumers for it — and
// no fields: at file level the payload carries the reads, not the field
// evidence.
export function QvdFileConsumingAppsDrawer({
  open,
  onOpenChange,
  runId,
  file,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  file: QvdFile | null;
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const query = useQuery(qvdFileConsumingAppsQueryOptions(runId, file?.id ?? "", open));

  return (
    <ConsumingAppsSheet
      open={open}
      onOpenChange={onOpenChange}
      name={file?.name}
      path={file?.path}
      query={query}
      restoreFocusRef={restoreFocusRef}
      showFields={false}
    />
  );
}
